import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  processFlightTicket,
  transformOCRData,
} from "../../../../api/ocrServices";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useBniTravelFlow from "../../../../hooks/useBniTravelFlow";
import useBniTravelFormData from "../../../../hooks/useBniTravelFormData";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { clearTravelQuote } from "../../../../redux/features/quoteSlice";
import { isFamilyAgeIneligible } from "../../../../utils";
import DateRangeInput from "../../../common/DateRangeInput/dateRangeInput";
import Input from "../../../common/Input/Input";
import FileUploadInput from "../../../common/newFileUploadInput/FileUploadInput";
import FormTabs from "../../../common/tabs/formTabs";
import TitleDescription from "../../../newMotorPolicy/titleDescription/titleDescription";
import NewTravelJourneyLayout from "../../layouts/NewTravelJourneyLayout";
import TravelBottomStickyBtnLayout from "../../layouts/TravelBottomStickyBtnLayout";

export const TRAVEL_DATE_SELECTION_FORM = "TRAVEL_DATE_SELECTION_FORM";

const TravelDateSelection = () => {
  const dispatch = useDispatch();
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const profileData = useSelector(selectProfileData);
  const translate = useLanguageTranslate();
  const isGuest = !loginTokenInfo?.token;

  // Resolve primary user's DOB from profile (logged-in) or basic info (guest).
  const getPrimaryUserDOB = () => {
    if (!isGuest && profileData?.DateOfBirth) return profileData.DateOfBirth;
    return travelFormData?.TRAVEL_BASIC_INFO?.birthDate || null;
  };

  const isPrimaryUserAgeIneligibleForFamily = () =>
    isFamilyAgeIneligible(getPrimaryUserDOB());
  const { handleNextSubstep, handleNextStep } = useBniTravelFlow();

  // Initialize form data state first with enhanced state structure
  const [formData, setFormData] = useState({
    hasFlightTickets: null,
    flightNumber: "",
    fromDate: null,
    toDate: null,
    planType: "", // No default selection for plan type
    isAnnual: false,
    // Enhanced state structure for conditional form display
    uploadedFiles: [],
    mockDataPopulated: false,
    mockDataSource: null, // Track data origin: 'upload' or 'manual'
    isUploading: false,
    uploadError: null,
    // OCR-specific state
    ocrProcessing: false,
    ocrData: null,
    showOCRResult: false,
    showOCRProcessingToUser: false, // Only show processing UI when user clicks continue
  });

  const { saveTravelDataInRedux, getTravelFormData, travelFormData } =
    useBniTravelFormData(TRAVEL_DATE_SELECTION_FORM);

  // Note: Quote prefetching is now handled in the main NewTravelPolicy component
  // This provides better performance and avoids duplicate API calls

  // Load saved form data on component mount - only run once to avoid loops
  useEffect(() => {
    const loadSavedData = () => {
      const savedFormData = getTravelFormData();
      console.log("Loading saved travel form data:", savedFormData);

      if (savedFormData) {
        // Convert date strings back to Date objects and ensure enhanced state structure
        // Note: Use explicit undefined checking for boolean fields to preserve false values
        const processedData = {
          hasFlightTickets:
            savedFormData.hasFlightTickets !== undefined
              ? savedFormData.hasFlightTickets
              : null,
          flightNumber: savedFormData.flightNumber || "",
          fromDate: savedFormData.fromDate
            ? new Date(savedFormData.fromDate)
            : null,
          toDate: savedFormData.toDate ? new Date(savedFormData.toDate) : null,
          planType: savedFormData.planType || "",
          isAnnual: savedFormData.isAnnual !== undefined ? savedFormData.isAnnual : false,
          // Enhanced state structure with defaults
          uploadedFiles: savedFormData.uploadedFiles || [],
          mockDataPopulated:
            savedFormData.mockDataPopulated !== undefined
              ? savedFormData.mockDataPopulated
              : false,
          mockDataSource: savedFormData.mockDataSource || null,
          isUploading:
            savedFormData.isUploading !== undefined
              ? savedFormData.isUploading
              : false,
          uploadError: savedFormData.uploadError || null,
          // OCR-specific state (don't persist these across sessions)
          ocrProcessing: false,
          ocrData: null,
          showOCRResult: false,
          showOCRProcessingToUser: false,
        };

        console.log("Processed form data for loading:", {
          hasFlightTickets: processedData.hasFlightTickets,
          fromDate: processedData.fromDate,
          toDate: processedData.toDate,
          planType: processedData.planType,
          mockDataPopulated: processedData.mockDataPopulated,
        });

        setFormData(processedData);
      } else {
        console.log("No saved travel form data found");
      }
    };

    loadSavedData();
  }, []); // Only run on component mount

  // CRITICAL FIX: Sync plan type changes from other components (e.g., when user adds family member in contact details)
  useEffect(() => {
    const savedFormData = getTravelFormData();

    if (
      savedFormData &&
      savedFormData.planType &&
      savedFormData.planType !== formData.planType
    ) {
      console.log(
        "🔄 PLAN TYPE SYNC: Updating dateSelection planType from:",
        formData.planType,
        "to:",
        savedFormData.planType
      );

      setFormData((prev) => ({
        ...prev,
        planType: savedFormData.planType,
      }));
    }
  }, [travelFormData]); // Watch the actual Redux state for changes

  // Cleanup stale upload data when returning to component
  useEffect(() => {
    // If we have uploaded files but no processing data (OCR or mock), it's likely stale data
    if (
      formData.uploadedFiles &&
      formData.uploadedFiles.length > 0 &&
      !formData.ocrData &&
      !formData.mockDataPopulated &&
      !formData.ocrProcessing &&
      !formData.isUploading
    ) {
      console.log("Detected stale upload data, clearing...");
      setFormData((prev) => ({
        ...prev,
        uploadedFiles: [],
        uploadError: null,
      }));
    }
  }, [
    formData.uploadedFiles,
    formData.ocrData,
    formData.mockDataPopulated,
    formData.ocrProcessing,
    formData.isUploading,
  ]);

  const [errors, setErrors] = useState({});

  // Error message translation function with comprehensive error categorization
  const getErrorMessage = (errorType, details = {}) => {
    switch (errorType) {
      case "FILE_SIZE_EXCEEDED":
        const maxSizeMB = Math.round(details.maxSizeKB / 1024) || 10;
        return {
          en: `File size exceeds ${maxSizeMB}MB limit. Please choose a smaller file.`,
          ar: `حجم الملف يتجاوز الحد المسموح ${maxSizeMB} ميجابايت. يرجى اختيار ملف أصغر.`,
        };
      case "INVALID_FILE_TYPE":
        return {
          en: "Please upload PDF, JPG, or PNG files only.",
          ar: "يرجى رفع ملفات PDF أو JPG أو PNG فقط.",
        };
      case "NETWORK_ERROR":
        return {
          en: "Network error. Please check your connection and try again.",
          ar: "خطأ في الشبكة. يرجى التحقق من الاتصال والمحاولة مرة أخرى.",
        };
      case "UPLOAD_TIMEOUT":
        return {
          en: "Upload timed out. Please try again.",
          ar: "انتهت مهلة الرفع. يرجى المحاولة مرة أخرى.",
        };
      case "SERVER_ERROR":
        return {
          en: "Server error occurred. Please try again later.",
          ar: "حدث خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً.",
        };
      case "TOKEN_ERROR":
        return {
          en: "Authentication failed. Please refresh the page and try again.",
          ar: "فشل في التوثيق. يرجى إعادة تحميل الصفحة والمحاولة مرة أخرى.",
        };
      case "FILE_READ_ERROR":
        return {
          en: "Failed to read file. The file may be corrupted.",
          ar: "فشل في قراءة الملف. قد يكون الملف تالفاً.",
        };
      case "MAX_FILES_EXCEEDED":
        const maxFiles = details.maxFiles || 3;
        return {
          en: `You can only upload up to ${maxFiles} files.`,
          ar: `يمكنك رفع حد أقصى ${maxFiles} ملفات.`,
        };
      case "UPLOAD_FAILED":
      default:
        return {
          en: "Upload failed. Please try again.",
          ar: "فشل في الرفع. يرجى المحاولة مرة أخرى.",
        };
    }
  };

  // Enhanced error handling for upload failures
  const handleUploadError = (errorType, details = {}, fileData = null) => {
    const errorMessages = getErrorMessage(errorType, details);
    const errorMessage = translate(errorMessages.en, errorMessages.ar);

    console.error("Upload error:", { errorType, details, fileData });

    // Update form state with error information
    setFormData((prev) => ({
      ...prev,
      isUploading: false,
      uploadError: {
        type: errorType,
        message: errorMessage,
        details: details,
        fileData: fileData,
        timestamp: Date.now(),
        canRetry: [
          "NETWORK_ERROR",
          "UPLOAD_TIMEOUT",
          "SERVER_ERROR",
          "TOKEN_ERROR",
        ].includes(errorType),
      },
    }));

    // Clear mock data if it was populated from a previous upload
    if (formData.mockDataSource === "upload") {
      setFormData((prev) => ({
        ...prev,
        flightNumber: "",
        fromDate: null,
        toDate: null,
        planType: "",
        isAnnual: false,
        mockDataPopulated: false,
        mockDataSource: null,
      }));
    }
  };

  // Retry upload function for recoverable errors
  const retryUpload = async (fileData) => {
    if (!fileData || !formData.uploadError) return;

    // Clear previous error
    setFormData((prev) => ({
      ...prev,
      uploadError: null,
      isUploading: true,
    }));

    // Retry the upload
    setTimeout(() => {
      handleFileUpload(fileData);
    }, 500);
  };

  // Switch to manual entry mode
  const switchToManualEntry = () => {
    setFormData((prev) => ({
      ...prev,
      hasFlightTickets: false,
      uploadedFiles: [],
      uploadError: null,
      mockDataPopulated: false,
      mockDataSource: null,
      flightNumber: "",
      fromDate: null,
      toDate: null,
      planType: "",
      isAnnual: false,
    }));

    // Clear any upload-related errors
    setErrors((prev) => ({
      ...prev,
      uploadedFiles: "",
    }));
  };

  // Mock ticket data generation service
  const generateMockTicketData = (uploadedFiles) => {
    // Use current date as base
    const currentDate = new Date();

    // Calculate departure date (+7 days from today)
    const departureDate = new Date(
      currentDate.getTime() + 7 * 24 * 60 * 60 * 1000
    );

    // Calculate return date (+14 days from today)
    const returnDate = new Date(
      currentDate.getTime() + 14 * 24 * 60 * 60 * 1000
    );

    // Determine plan type based on number of files
    const planType =
      uploadedFiles && uploadedFiles.length > 1 ? "family" : "individual";

    return {
      flightNumber: "GF285",
      fromDate: departureDate,
      toDate: returnDate,
      planType: planType,
      mockDataPopulated: true,
      mockDataSource: "upload",
    };
  };

  // Helper function to populate form with OCR or mock data
  const populateFormWithData = (uploadedFiles, ocrData = null) => {
    let travelData;

    if (ocrData) {
      // Use OCR data if available
      try {
        travelData = transformOCRData(ocrData);
        travelData.mockDataSource = "ocr";
      } catch (error) {
        console.error("Error transforming OCR data:", error);
        toast.error(
          translate(
            "Failed to process ticket data. Please enter details manually.",
            "فشل في معالجة بيانات التذكرة. يرجى إدخال التفاصيل يدوياً."
          )
        );
        // Fall back to mock data
        travelData = generateMockTicketData(uploadedFiles);
      }
    } else {
      // Use mock data as fallback
      travelData = generateMockTicketData(uploadedFiles);
    }

    console.log("Travel data populated:", {
      flightNumber: travelData.flightNumber,
      fromDate: travelData.fromDate?.toLocaleDateString(),
      toDate: travelData.toDate?.toLocaleDateString(),
      planType: travelData.planType,
      source: travelData.mockDataSource,
    });

    setFormData((prev) => ({
      ...prev,
      flightNumber: travelData.flightNumber || prev.flightNumber,
      fromDate: travelData.fromDate || prev.fromDate,
      toDate: travelData.toDate || prev.toDate,
      planType: travelData.planType || prev.planType,
      mockDataPopulated: true,
      mockDataSource: travelData.mockDataSource,
      uploadedFiles: uploadedFiles || prev.uploadedFiles,
      uploadError: null, // Clear any upload errors when data is populated
      ocrData: ocrData,
    }));

    // Validate OCR/populated data for date constraints
    const newErrors = {};
    if (travelData.fromDate && travelData.toDate) {
      // Check if return date is after departure date
      if (travelData.toDate <= travelData.fromDate) {
        newErrors.toDate = translate(
          "To date must be after from date",
          "تاريخ العودة يجب أن يكون بعد تاريخ الذهاب"
        );
      } else {
        // Check one-year validation rule
        const oneYearAfterDeparture = new Date(travelData.fromDate);
        oneYearAfterDeparture.setFullYear(
          oneYearAfterDeparture.getFullYear() + 1
        );
        oneYearAfterDeparture.setDate(oneYearAfterDeparture.getDate() - 1);
        if (travelData.toDate > oneYearAfterDeparture) {
          newErrors.toDate = translate(
            "The return date cannot be more than one year after the departure date.",
            "لا يمكن أن يتجاوز تاريخ العودة سنة واحدة بعد تاريخ المغادرة"
          );
        }
      }
    }

    // Set errors (empty object if no validation errors)
    setErrors(newErrors);
  };

  // Legacy function for backward compatibility
  const populateFormWithMockData = (uploadedFiles) => {
    populateFormWithData(uploadedFiles, null);
  };

  // OCR processing function - runs silently in background after file upload
  const processFileWithOCR = async (file) => {
    try {
      setFormData((prev) => ({ ...prev, ocrProcessing: true }));

      console.log("Background OCR processing started for:", file.name);

      const result = await processFlightTicket(file);

      if (result.success && result.data?.output?.status) {
        // OCR successful - data ready for when user clicks continue
        console.log(
          "Background OCR processing completed successfully:",
          result.data
        );

        // Save OCR data and navigate to OCR result page
        const dataToSave = {
          ...formData,
          ocrData: result.data,
          ocrProcessing: false,
          showOCRResult: true,
          uploadedFiles: [{ fileName: file.name, file }],
        };

        // Save to Redux immediately
        saveTravelDataInRedux(dataToSave);

        setFormData((prev) => ({
          ...prev,
          ocrProcessing: false,
          ocrData: result.data,
          showOCRProcessingToUser: false,
          showOCRResult: true,
        }));

        // Auto-navigate to OCR result page after a short delay
        setTimeout(() => {
          handleNextSubstep(); // This will navigate to 'ocr_result' substep
        }, 1000);
      } else {
        // OCR failed - show error toast
        console.warn(
          "Background OCR processing failed:",
          result.error || result.data
        );

        // Show error toast
        toast.error(
          translate(
            "Failed to process ticket data. Please fill the form manually.",
            "فشل في معالجة بيانات التذكرة. يرجى ملء النموذج يدوياً."
          )
        );

        setFormData((prev) => ({
          ...prev,
          ocrProcessing: false,
          showOCRProcessingToUser: false, // Reset user-facing loading state
        }));
      }
    } catch (error) {
      console.error("Background OCR processing error:", error);

      // Show error toast for processing errors
      toast.error(
        translate(
          "Failed to process ticket data. Please fill the form manually.",
          "فشل في معالجة بيانات التذكرة. يرجى ملء النموذج يدوياً."
        )
      );

      setFormData((prev) => ({
        ...prev,
        ocrProcessing: false,
        showOCRProcessingToUser: false, // Reset user-facing loading state
      }));
    }
  };

  // Enhanced file upload handlers with comprehensive error handling
  const handleFileUpload = async (fileData) => {
    console.log("File uploaded:", fileData);

    // Clear any previous upload errors when starting a new upload
    setFormData((prev) => ({
      ...prev,
      uploadError: null,
    }));

    // Validate file data and check for errors from FileUploadInput component
    if (!fileData) {
      handleUploadError("UPLOAD_FAILED", { reason: "No file data received" });
      return;
    }

    // Check if file data indicates an upload error (from FileUploadInput component)
    if (fileData.error) {
      // Categorize the error based on the error message
      let errorType = "UPLOAD_FAILED";
      const errorMessage = fileData.error.toLowerCase();

      if (errorMessage.includes("size") && errorMessage.includes("kb")) {
        errorType = "FILE_SIZE_EXCEEDED";
      } else if (
        errorMessage.includes("format") ||
        errorMessage.includes("type")
      ) {
        errorType = "INVALID_FILE_TYPE";
      } else if (errorMessage.includes("network")) {
        errorType = "NETWORK_ERROR";
      } else if (errorMessage.includes("timeout")) {
        errorType = "UPLOAD_TIMEOUT";
      } else if (errorMessage.includes("token")) {
        errorType = "TOKEN_ERROR";
      } else if (errorMessage.includes("read")) {
        errorType = "FILE_READ_ERROR";
      } else if (errorMessage.includes("server")) {
        errorType = "SERVER_ERROR";
      }

      handleUploadError(errorType, { originalError: fileData.error }, fileData);
      return;
    }

    // Validate file properties
    if (!fileData.fileName || !fileData.file) {
      handleUploadError(
        "UPLOAD_FAILED",
        { reason: "Invalid file data structure" },
        fileData
      );
      return;
    }

    // Check file size (additional client-side validation)
    const maxSizeKB = 10240; // 10MB
    if (fileData.file.size && fileData.file.size / 1024 > maxSizeKB) {
      handleUploadError(
        "FILE_SIZE_EXCEEDED",
        { maxSizeKB, actualSizeKB: Math.round(fileData.file.size / 1024) },
        fileData
      );
      return;
    }

    // Check file type (additional client-side validation)
    const validTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (fileData.file.type && !validTypes.includes(fileData.file.type)) {
      handleUploadError(
        "INVALID_FILE_TYPE",
        { validTypes, actualType: fileData.file.type },
        fileData
      );
      return;
    }

    // Update uploadedFiles array with new file
    setFormData((prev) => {
      const updatedFiles = [...prev.uploadedFiles];

      // Check if adding this file would exceed the maximum
      const maxFiles = 3;
      if (updatedFiles.length >= maxFiles) {
        // Don't add the file, but trigger an error
        setTimeout(
          () => handleUploadError("MAX_FILES_EXCEEDED", { maxFiles }),
          0
        );
        return prev;
      }

      // Check if file already exists (avoid duplicates)
      const existingFileIndex = updatedFiles.findIndex(
        (f) => f.fileName === fileData.fileName
      );
      if (existingFileIndex >= 0) {
        // Replace existing file
        updatedFiles[existingFileIndex] = fileData;
      } else {
        // Add new file
        updatedFiles.push(fileData);
      }

      const newState = {
        ...prev,
        uploadedFiles: updatedFiles,
        isUploading: false,
        uploadError: null, // Clear any previous errors on successful upload
      };

      return newState;
    });

    // Process OCR in background immediately after file upload for better UX
    // Don't show loading states until user clicks continue
    if (fileData && fileData.file) {
      try {
        console.log(
          "Starting background OCR processing for:",
          fileData.fileName
        );
        processFileWithOCR(fileData.file);
      } catch (error) {
        console.error("Error starting background OCR:", error);
        // Silently fail - don't show errors until continue is clicked
      }
    }

    // Clear file upload errors when files are successfully uploaded
    if (errors.uploadedFiles) {
      setErrors((prev) => ({ ...prev, uploadedFiles: "" }));
    }
  };

  const handleFileRemove = (fileToRemove) => {
    console.log("File removed:", fileToRemove);

    setFormData((prev) => {
      let updatedFiles;

      if (typeof fileToRemove === "string") {
        // Remove by fileName
        updatedFiles = prev.uploadedFiles.filter(
          (f) => f.fileName !== fileToRemove
        );
      } else {
        // Remove by entire file object or other criteria
        updatedFiles = prev.uploadedFiles.filter((f) => f !== fileToRemove);
      }

      // If no files remain, reset mock data state and clear errors
      if (updatedFiles.length === 0) {
        return {
          ...prev,
          uploadedFiles: [],
          mockDataPopulated: false,
          mockDataSource: null,
          flightNumber: "",
          fromDate: null,
          toDate: null,
          planType: "",
          isAnnual: false,
          uploadError: null, // Clear upload errors when all files are removed
        };
      }

      // Re-populate with remaining files
      const newState = {
        ...prev,
        uploadedFiles: updatedFiles,
        uploadError: null, // Clear upload errors when files are successfully managed
      };

      // Re-populate mock data with remaining files (only if not showing OCR result)
      if (!prev.showOCRResult) {
        try {
          populateFormWithMockData(updatedFiles);
        } catch (error) {
          console.error(
            "Error re-populating mock data after file removal:",
            error
          );
          // Don't show error to user for this scenario, just log it
        }
      }

      return newState;
    });

    // Clear any validation errors
    setErrors((prev) => ({ ...prev, uploadedFiles: "" }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing for immediate UI feedback
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFlightTicketsChange = (hasTickets) => {
    setFormData((prev) => ({
      ...prev,
      hasFlightTickets: hasTickets,
      flightNumber: hasTickets ? prev.flightNumber : "",
      // Reset upload-related state when changing flight ticket status
      uploadedFiles: hasTickets ? prev.uploadedFiles : [],
      uploadError: hasTickets ? prev.uploadError : null,
      mockDataPopulated: hasTickets ? prev.mockDataPopulated : false,
      mockDataSource: hasTickets ? prev.mockDataSource : null,
      isAnnual: false,
      fromDate: hasTickets ? prev.fromDate : null,
      toDate: hasTickets ? prev.toDate : null,
      planType: hasTickets ? prev.planType : "",
      // Reset OCR state
      ocrProcessing: hasTickets ? prev.ocrProcessing : false,
      ocrData: hasTickets ? prev.ocrData : null,
      showOCRResult: hasTickets ? prev.showOCRResult : false,
      showOCRProcessingToUser: hasTickets
        ? prev.showOCRProcessingToUser
        : false,
    }));

    // Clear error when user makes selection for immediate UI feedback
    if (errors.hasFlightTickets) {
      setErrors((prev) => ({ ...prev, hasFlightTickets: "" }));
    }
  };

  const handlePlanTypeChange = (planType) => {
    console.log("🔄 DATE SELECTION: Plan type changed to:", planType);

    // CRITICAL: Clear Redux cache when planType changes in date selection
    console.log(
      "🗑️ CLEARING REDUX QUOTE CACHE - planType changed in date selection"
    );
    dispatch(clearTravelQuote());

    setFormData((prev) => ({
      ...prev,
      planType,
    }));

    // Clear error when user makes selection for immediate UI feedback
    if (errors.planType) {
      setErrors((prev) => ({ ...prev, planType: "" }));
    }
  };

  const handleAnnualToggle = (checked) => {
    console.log("🔄 DATE SELECTION: Annual policy toggled to:", checked);

    dispatch(clearTravelQuote());

    if (checked && formData.fromDate) {
      const annualToDate = new Date(formData.fromDate);
      annualToDate.setFullYear(annualToDate.getFullYear() + 1);
      annualToDate.setDate(annualToDate.getDate() - 1);
      setFormData((prev) => ({
        ...prev,
        isAnnual: true,
        toDate: annualToDate,
      }));
      if (errors.toDate) {
        setErrors((prev) => ({ ...prev, toDate: "" }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        isAnnual: checked,
        toDate: checked ? prev.toDate : null,
      }));
    }
  };

  const handleDateRangeChange = (startDate, endDate) => {
    console.log(
      "🔄 DATE SELECTION: Dates changed to:",
      startDate,
      "to",
      endDate
    );

    // CRITICAL: Clear Redux cache when dates change
    console.log("🗑️ CLEARING REDUX QUOTE CACHE - travel dates changed");
    dispatch(clearTravelQuote());

    setFormData((prev) => {
      let newToDate = endDate;
      if (prev.isAnnual && startDate) {
        const annualToDate = new Date(startDate);
        annualToDate.setFullYear(annualToDate.getFullYear() + 1);
        annualToDate.setDate(annualToDate.getDate() - 1);
        newToDate = annualToDate;
      }
      return {
        ...prev,
        fromDate: startDate,
        toDate: newToDate,
      };
    });

    // Real-time validation and error clearing
    const effectiveEndDate = formData.isAnnual && startDate
      ? (() => { const d = new Date(startDate); d.setFullYear(d.getFullYear() + 1); d.setDate(d.getDate() - 1); return d; })()
      : endDate;
    if (startDate && effectiveEndDate) {
      const newErrors = { ...errors };

      // Clear existing date errors if dates are valid
      if (effectiveEndDate > startDate) {
        // Check one-year validation rule
        const oneYearAfterDeparture = new Date(startDate);
        oneYearAfterDeparture.setFullYear(
          oneYearAfterDeparture.getFullYear() + 1
        );
        oneYearAfterDeparture.setDate(oneYearAfterDeparture.getDate() - 1);

        if (effectiveEndDate > oneYearAfterDeparture) {
          newErrors.toDate = translate(
            "The return date cannot be more than one year after the departure date.",
            "لا يمكن أن يتجاوز تاريخ العودة سنة واحدة بعد تاريخ المغادرة"
          );
        } else {
          // Valid date range, clear errors
          newErrors.fromDate = "";
          newErrors.toDate = "";
        }
      } else {
        // End date is not after start date
        newErrors.toDate = translate(
          "To date must be after from date",
          "تاريخ العودة يجب أن يكون بعد تاريخ الذهاب"
        );
      }

      setErrors(newErrors);
    } else {
      // Clear errors when user selects dates for immediate UI feedback
      if (errors.fromDate || errors.toDate) {
        setErrors((prev) => ({
          ...prev,
          fromDate: startDate ? "" : prev.fromDate,
          toDate: endDate ? "" : prev.toDate,
        }));
      }
    }
  };

  // Enhanced form validation with comprehensive error handling and performance optimization
  const validateForm = () => {
    const newErrors = {};

    // Primary validation: Flight tickets selection
    if (formData.hasFlightTickets === null) {
      newErrors.hasFlightTickets = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Path-specific validation
    if (formData.hasFlightTickets === true) {
      // Upload path validation
      if (!formData.mockDataPopulated) {
        // Check for ongoing upload errors that need resolution
        if (formData.uploadError && formData.uploadError.canRetry) {
          newErrors.uploadedFiles = translate(
            "Please resolve the upload error above before continuing.",
            "يرجى حل خطأ الرفع أعلاه قبل المتابعة."
          );
        }
        // Validate file upload requirement (only if no current upload error)
        else if (
          !formData.uploadedFiles ||
          formData.uploadedFiles.length === 0
        ) {
          newErrors.uploadedFiles = translate(
            "Please upload your flight ticket.",
            "يرجى رفع تذكرة الطيران الخاصة بك."
          );
        }
      }

      // Populated form validation (when mock data is available)
      if (formData.mockDataPopulated) {
        // Flight number validation (required for uploaded tickets)
        if (!formData.flightNumber || !formData.flightNumber.trim()) {
          newErrors.flightNumber = translate(
            "This field is required.",
            "هذا الحقل مطلوب."
          );
        }

        // Date validation
        if (!formData.fromDate) {
          newErrors.fromDate = translate(
            "This field is required.",
            "هذا الحقل مطلوب."
          );
        }

        if (!formData.toDate) {
          newErrors.toDate = translate(
            "This field is required.",
            "هذا الحقل مطلوب."
          );
        }

        // Cross-field date validation
        if (
          formData.fromDate &&
          formData.toDate &&
          formData.toDate <= formData.fromDate
        ) {
          newErrors.toDate = translate(
            "To date must be after from date",
            "تاريخ العودة يجب أن يكون بعد تاريخ الذهاب"
          );
        }

        // Business rule: Maximum travel duration validation (one year)
        if (formData.fromDate && formData.toDate) {
          const oneYearAfterDeparture = new Date(formData.fromDate);
          oneYearAfterDeparture.setFullYear(
            oneYearAfterDeparture.getFullYear() + 1
          );
          oneYearAfterDeparture.setDate(oneYearAfterDeparture.getDate() - 1);
          if (formData.toDate > oneYearAfterDeparture) {
            newErrors.toDate = translate(
              "The return date cannot be more than one year after the departure date.",
              "لا يمكن أن يتجاوز تاريخ العودة سنة واحدة بعد تاريخ المغادرة"
            );
          }
        }

        // Business rule: departure must be at least tomorrow (same-day travel not allowed)
        if (formData.fromDate) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (formData.fromDate <= today) {
            newErrors.fromDate = translate(
              "Travel start date must be at least one day in the future",
              "يجب أن يكون تاريخ بداية السفر على الأقل يوماً واحداً في المستقبل"
            );
          }

          const maxFutureDate = new Date();
          maxFutureDate.setFullYear(maxFutureDate.getFullYear() + 1);
          if (formData.fromDate > maxFutureDate) {
            newErrors.fromDate = translate(
              "Travel date cannot be more than 1 year in the future",
              "لا يمكن أن يكون تاريخ السفر أكثر من سنة في المستقبل"
            );
          }
        }

        // Plan type validation
        if (!formData.planType) {
          newErrors.planType = translate(
            "This field is required.",
            "هذا الحقل مطلوب."
          );
        }
      }
    }

    // Manual entry validation (hasFlightTickets === false)
    if (formData.hasFlightTickets === false) {
      // Required fields for manual entry (NOTE: flightNumber is NOT required)
      if (!formData.fromDate) {
        newErrors.fromDate = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }

      if (!formData.toDate) {
        newErrors.toDate = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }

      if (!formData.planType) {
        newErrors.planType = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }

      // Cross-field validations for manual entry
      if (formData.fromDate && formData.toDate) {
        // Validate that to date is after from date
        if (formData.toDate <= formData.fromDate) {
          newErrors.toDate = translate(
            "To date must be after from date",
            "تاريخ العودة يجب أن يكون بعد تاريخ الذهاب"
          );
        }

        // Business rule: Maximum travel duration validation (one year)
        const oneYearAfterDeparture = new Date(formData.fromDate);
        oneYearAfterDeparture.setFullYear(
          oneYearAfterDeparture.getFullYear() + 1
        );
        oneYearAfterDeparture.setDate(oneYearAfterDeparture.getDate() - 1);
        if (formData.toDate > oneYearAfterDeparture) {
          newErrors.toDate = translate(
            "The return date cannot be more than one year after the departure date.",
            "لا يمكن أن يتجاوز تاريخ العودة سنة واحدة بعد تاريخ المغادرة"
          );
        }
      }

      // Business rule: departure must be at least tomorrow (same-day travel not allowed)
      if (formData.fromDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (formData.fromDate <= today) {
          newErrors.fromDate = translate(
            "Travel start date must be at least one day in the future",
            "يجب أن يكون تاريخ بداية السفر على الأقل يوماً واحداً في المستقبل"
          );
        }

        const maxFutureDate = new Date();
        maxFutureDate.setFullYear(maxFutureDate.getFullYear() + 1);
        if (formData.fromDate > maxFutureDate) {
          newErrors.fromDate = translate(
            "Travel date cannot be more than 1 year in the future",
            "لا يمكن أن يكون تاريخ السفر أكثر من سنة في المستقبل"
          );
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    // If OCR is still processing when user clicks continue, show loading state
    if (
      formData.hasFlightTickets === true &&
      formData.uploadedFiles.length > 0 &&
      !formData.ocrData &&
      formData.ocrProcessing &&
      !formData.mockDataPopulated
    ) {
      console.log("OCR still processing, showing loading state to user");
      // Show processing UI to user since they clicked continue
      setFormData((prev) => ({ ...prev, showOCRProcessingToUser: true }));
      // Let the existing OCR processing continue, loading state will be shown via button
      return;
    }

    // If user has uploaded files but OCR failed or hasn't started, show error
    if (
      formData.hasFlightTickets === true &&
      formData.uploadedFiles.length > 0 &&
      !formData.ocrData &&
      !formData.ocrProcessing &&
      !formData.mockDataPopulated
    ) {
      console.log("OCR failed or did not start, falling back to manual mode");
      // OCR failed or didn't start - fall back to manual form filling
      toast.error(
        translate(
          "Could not read ticket data. Please fill the form manually.",
          "لا يمكن قراءة بيانات التذكرة. يرجى ملء النموذج يدوياً."
        )
      );

      setFormData((prev) => ({
        ...prev,
        hasFlightTickets: false, // Switch to manual mode
        isAnnual: false,
      }));
      return;
    }

    // Clear any previous upload errors that might have been dismissed but not cleared
    if (formData.uploadError && !formData.uploadError.canRetry) {
      setFormData((prev) => ({ ...prev, uploadError: null }));
    }

    // Block 66+ users from continuing with the family plan.
    // The inline error under the plan type tabs gives the visible feedback.
    if (
      formData.planType === "family" &&
      isPrimaryUserAgeIneligibleForFamily()
    ) {
      return;
    }

    if (validateForm()) {
      try {
        // Save form data to Redux (convert Date objects to strings for serialization)
        const dataToSave = {
          ...formData,
          fromDate: formData.fromDate ? formData.fromDate.toISOString() : null,
          toDate: formData.toDate ? formData.toDate.toISOString() : null,
          // Clear upload error and OCR data from saved data as they're session-specific
          uploadError: null,
          ocrProcessing: false,
          showOCRResult: false,
        };

        saveTravelDataInRedux(dataToSave);

        // Note: Quote prefetching happens at travel flow start for optimal performance
        // This avoids duplicate API calls and provides better user experience

        // Log successful form completion for debugging
        console.log("Travel date selection completed successfully:", {
          hasFlightTickets: formData.hasFlightTickets,
          mockDataPopulated: formData.mockDataPopulated,
          uploadedFilesCount: formData.uploadedFiles?.length || 0,
          planType: formData.planType,
          ocrProcessed: !!formData.ocrData,
        });

        // If user chose "No" for flight tickets, skip OCR substeps and go to next step
        if (formData.hasFlightTickets === false) {
          handleNextStep();
        } else if (
          formData.hasFlightTickets === true &&
          formData.ocrData &&
          formData.showOCRResult
        ) {
          // If user has uploaded files and OCR is complete, navigate to OCR result substep
          handleNextSubstep();
        } else {
          // For manual entry (no upload) or other cases, continue to next step
          handleNextStep();
        }
      } catch (error) {
        console.error("Error saving travel data:", error);

        // Show a user-friendly error message
        setFormData((prev) => ({
          ...prev,
          uploadError: {
            type: "SAVE_ERROR",
            message: translate(
              "Failed to save your information. Please try again.",
              "فشل في حفظ معلوماتك. يرجى المحاولة مرة أخرى."
            ),
            canRetry: false,
            timestamp: Date.now(),
          },
        }));
      }
    } else {
      // Scroll to first error if validation fails
      const firstErrorField = document.querySelector(".field-err");
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  // Enhanced field validation function for populated forms
  const validateFields = (data) => {
    // Validate flight number (required for populated forms from upload)
    if (!data.flightNumber || !data.flightNumber.trim()) return false;

    // Validate dates
    if (!data.fromDate || !data.toDate) return false;

    // Validate that to date is after from date
    if (data.toDate <= data.fromDate) return false;

    // Validate one-year rule - return date cannot be more than one year after departure
    const oneYearAfterDeparture = new Date(data.fromDate);
    oneYearAfterDeparture.setFullYear(oneYearAfterDeparture.getFullYear() + 1);
    oneYearAfterDeparture.setDate(oneYearAfterDeparture.getDate() - 1);
    if (data.toDate > oneYearAfterDeparture) return false;

    // Validate plan type
    if (!data.planType) return false;

    // All validations passed
    return true;
  };

  // Performance-optimized Continue button state calculation using useMemo
  const isContinueDisabled = useMemo(() => {
    // Requirement 1: WHEN no selection is made THEN Continue button should be disabled
    if (formData.hasFlightTickets === null) return true;

    // Disable if currently uploading or showing OCR processing to user (loading state)
    if (formData.isUploading || formData.showOCRProcessingToUser) return true;

    // Disable if there's an active upload error that requires resolution
    if (formData.uploadError && formData.uploadError.canRetry) return true;

    // Disable if primary user is 66+ and has selected the family plan
    if (
      formData.planType === "family" &&
      isPrimaryUserAgeIneligibleForFamily()
    ) {
      return true;
    }

    // Handle different paths based on flight tickets selection
    if (formData.hasFlightTickets === false) {
      // Requirement 2: Manual entry path - all fields required (fromDate, toDate, planType)
      // Note: flightNumber is NOT required for manual entry since users don't have tickets
      return !formData.fromDate || !formData.toDate || !formData.planType;
    }

    if (formData.hasFlightTickets === true) {
      if (!formData.mockDataPopulated) {
        // Requirement 3: Upload required - WHEN no files are uploaded THEN Continue button should remain disabled
        if (!formData.uploadedFiles || formData.uploadedFiles.length === 0) {
          return true;
        }
        // NEW: Keep button disabled while OCR is processing after file upload
        if (formData.ocrProcessing) {
          return true;
        }
        // Requirement 3: WHEN files are successfully uploaded AND OCR processing is done THEN Continue button should be enabled
        return false;
      } else {
        // Requirement 4: Mock data populated - validate all populated fields
        // WHEN mock data is populated THEN Continue button should be enabled (if validation passes)
        // WHEN editing populated data THEN form validation should work on the modified data
        return !validateFields(formData);
      }
    }

    return false;
  }, [
    formData.hasFlightTickets,
    formData.isUploading,
    formData.showOCRProcessingToUser,
    formData.uploadError,
    formData.fromDate,
    formData.toDate,
    formData.planType,
    formData.uploadedFiles,
    formData.mockDataPopulated,
    formData.showOCRResult,
    formData.flightNumber,
    formData.ocrProcessing,
  ]);

  return (
    <div className="w-full">
      <NewTravelJourneyLayout is_hide_back_btn={false}>
        <TravelBottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_disabled={isContinueDisabled}
          isLoading={formData.showOCRProcessingToUser}
          btn_label={translate("Continue", "التالي")}
        >
          <div className="w-[32%] max767:w-full mx-auto ipad:min-w-[66%] max767:px-4 max767:pb-24">
            <TitleDescription
              title={translate("About Your Trip", " تفاصيل عن رحلتك")}
              description={translate(
                "Welcome! Let's start with some basic information.",
                ".مرحبًا بك! لنبدأ ببعض المعلومات الأساسية"
              )}
              className="max767:-mt-0"
            />

            <div className="w-full mb-6">
              {/* Flight Tickets Question - Always visible */}
              <FormTabs
                label={translate(
                  "Do you have a travel flight ticket?",
                  "هل لديك تذاكر سفر؟"
                )}
                tabs={[
                  {
                    type: true,
                    label_en: "Yes",
                    label_ar: "نعم",
                  },
                  {
                    type: false,
                    label_en: "No",
                    label_ar: "لا",
                  },
                ]}
                activeTab={formData.hasFlightTickets}
                onTabClick={(value) => handleFlightTicketsChange(value)}
                error={errors.hasFlightTickets}
                className="mb-6"
              />

              {/* File Upload Section - Show when user has flight tickets but hasn't uploaded files yet */}
              {formData.hasFlightTickets === true &&
                !formData.mockDataPopulated && (
                  <div className="mb-6 mt-6">
                    {/* Informative section about why to upload tickets */}
                    <div className="mb-6 p-4 bg-[#F0F8F0] border border-[#D1E7DD] rounded-lg">
                      <h3 className="text-[#1A2024] text-lg font-medium mb-3">
                        {translate(
                          "Why upload your tickets?",
                          "لماذا عليك تحميل تذاكرك؟"
                        )}
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-start space-x-2 rtl:space-x-reverse">
                          <div className="flex-shrink-0 mt-1">
                            <svg
                              className="w-4 h-4 text-green-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <p className="text-sm text-[#000]">
                            {translate(
                              "Auto-fill: Instant ticket scanning saves you from manual data entry",
                              "الملء التلقائي: المسح الفوري للتذاكر يوفر عليك إدخال المعلومات بشكل يدوي"
                            )}
                          </p>
                        </div>
                        <div className="flex items-start space-x-2 rtl:space-x-reverse">
                          <div className="flex-shrink-0 mt-1">
                            <svg
                              className="w-4 h-4 text-green-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <p className="text-sm text-[#000]">
                            {translate(
                              "Accurate coverage: Get precise protection for your exact travel dates",
                              "تغطية دقيقة: احصل على تغطية دقيقة لتواريخ سفرك المحددة"
                            )}
                          </p>
                        </div>
                        <div className="flex items-start space-x-2 rtl:space-x-reverse">
                          <div className="flex-shrink-0 mt-1">
                            <svg
                              className="w-4 h-4 text-green-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <p className="text-sm text-[#000]">
                            {translate(
                              "Faster policy: Get covered in seconds with error-free processing",
                              "وثيقة أسرع: احصل على الوثيقة في ثوانٍ مع معالجة خالية من الأخطاء"
                            )}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-[#5B6871] mt-3">
                        {translate(
                          "Your ticket information is used only for policy creation and claims verification.",
                          "يتم استخدام معلومات تذكرتك فقط لإنشاء الوثيقة والتحقق من المطالبات."
                        )}
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px] mb-3 block">
                        {translate(
                          "Upload Your Flight Ticket",
                          "قم بتحميل تذاكر الطيران الخاصة بك"
                        )}
                      </label>
                      <p className="text-sm text-[#6B7682] mb-4">
                        {translate(
                          "Please upload your flight ticket (PDF, JPG, PNG). Maximum 1 file, up to 10 MB.",
                          "يرجى رفع تذكرة الطيران الخاصة بك (PDF، JPG، PNG). حد أقصى 1 ملف، حتى 10 ميجابايت."
                        )}
                      </p>
                      <FileUploadInput
                        name="flight_tickets"
                        onFileSelect={handleFileUpload}
                        onFileRemove={handleFileRemove}
                        validTypes={[
                          "application/pdf",
                          "image/jpeg",
                          "image/png",
                        ]}
                        maxSizeKB={10240} // 10MB limit
                        uploadToServer={true}
                        multiple={true}
                        maxFiles={1}
                        className="mb-4"
                        disabled={
                          formData.isUploading ||
                          formData.ocrProcessing ||
                          (formData.uploadError &&
                            formData.uploadError.canRetry)
                        }
                        initialFiles={formData.uploadedFiles || []}
                      />

                      {/* File upload validation error */}
                      {errors.uploadedFiles && (
                        <p className="field-err text-red-500 text-sm mt-1 text-left rtl:text-right">
                          {errors.uploadedFiles}
                        </p>
                      )}
                    </div>

                    {/* OCR Processing status - show when OCR is processing after file upload */}
                    {formData.ocrProcessing && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <p className="text-sm text-blue-700">
                            {translate(
                              "Processing ticket data...",
                              "جاري معالجة بيانات التذكرة..."
                            )}
                          </p>
                        </div>
                        <p className="text-xs text-blue-600 mt-1">
                          {translate(
                            "Please wait while we extract your travel information...",
                            "يرجى الانتظار بينما نستخرج معلومات السفر الخاصة بك..."
                          )}
                        </p>
                      </div>
                    )}

                    {/* Upload status messages */}
                    {formData.isUploading && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <p className="text-sm text-blue-700">
                            {translate(
                              "Uploading flight ticket...",
                              "جاري رفع تذكرة الطيران..."
                            )}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Enhanced upload error display with recovery options */}
                    {formData.uploadError && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start space-x-3 rtl:space-x-reverse">
                          <div className="flex-shrink-0">
                            <svg
                              className="w-5 h-5 text-red-600 mt-0.5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-red-800 mb-1">
                              {translate("Upload Failed", "فشل في الرفع")}
                            </h4>
                            <p className="text-sm text-red-700 mb-3">
                              {formData.uploadError.message}
                            </p>

                            <div className="flex flex-col sm:flex-row gap-2">
                              {/* Retry button for recoverable errors */}
                              {formData.uploadError.canRetry &&
                                formData.uploadError.fileData && (
                                  <button
                                    onClick={() =>
                                      retryUpload(formData.uploadError.fileData)
                                    }
                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                    disabled={formData.isUploading}
                                  >
                                    <svg
                                      className="-ml-0.5 mr-2 h-4 w-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                      />
                                    </svg>
                                    {translate("Try Again", "حاول مرة أخرى")}
                                  </button>
                                )}

                              {/* Switch to manual entry button */}
                              <button
                                onClick={switchToManualEntry}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                              >
                                <svg
                                  className="-ml-0.5 mr-2 h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                                {translate(
                                  "Enter Details Manually",
                                  "إدخال التفاصيل يدوياً"
                                )}
                              </button>

                              {/* Clear error button */}
                              <button
                                onClick={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    uploadError: null,
                                  }))
                                }
                                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                              >
                                <svg
                                  className="-ml-0.5 mr-2 h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                                {translate("Dismiss", "إغلاق")}
                              </button>
                            </div>

                            {/* Additional help text for persistent failures */}
                            {formData.uploadError.type === "NETWORK_ERROR" && (
                              <p className="text-xs text-red-600 mt-2">
                                {translate(
                                  "If this error persists, you can enter your travel details manually by clicking 'Enter Details Manually' above.",
                                  "إذا استمر هذا الخطأ، يمكنك إدخال تفاصيل السفر يدوياً بالضغط على 'إدخال التفاصيل يدوياً' أعلاه."
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show upload success and file count */}
                    {formData.uploadedFiles &&
                      formData.uploadedFiles.length > 0 &&
                      !formData.isUploading &&
                      (formData.ocrData || formData.mockDataPopulated) && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-700">
                            {translate(
                              `✓ ${formData.uploadedFiles.length} file(s) uploaded successfully`,
                              `✓ تم رفع ${formData.uploadedFiles.length} ملف بنجاح`
                            )}
                          </p>
                        </div>
                      )}
                  </div>
                )}

              {/* Show mock data status with enhanced messaging */}
              {formData.mockDataPopulated &&
                formData.mockDataSource === "upload" && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start space-x-2 rtl:space-x-reverse">
                      <div className="flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-green-600 mt-0.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-green-700 font-medium">
                          {translate(
                            "Travel data extracted successfully!",
                            "تم استخراج بيانات السفر بنجاح!"
                          )}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          {translate(
                            "We've populated your travel information from the uploaded tickets. You can edit any field if needed.",
                            "لقد قمنا بملء معلومات السفر من التذاكر المرفوعة. يمكنك تعديل أي حقل حسب الحاجة."
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {/* Form fields for users with flight tickets and populated mock data */}
              {formData.hasFlightTickets === true &&
                formData.mockDataPopulated && (
                  <>
                    {/* Flight Number field */}
                    <Input
                      type="text"
                      name="flightNumber"
                      label={translate("Flight Number", "رقم الرحلة")}
                      value={formData.flightNumber}
                      onChange={handleInputChange}
                      placeholder={translate(
                        "Enter flight number",
                        "أدخل رقم الرحلة"
                      )}
                      error={errors.flightNumber}
                    />

                    {/* Annual Policy Toggle */}
                    <FormTabs
                      label={translate("Do you want an annual policy?", "هل تريد بوليصة سنوية؟")}
                      tabs={[
                        {
                          type: true,
                          label_en: "Yes",
                          label_ar: "نعم",
                        },
                        {
                          type: false,
                          label_en: "No",
                          label_ar: "لا",
                        },
                      ]}
                      activeTab={formData.isAnnual}
                      onTabClick={(value) => handleAnnualToggle(value)}
                      className="mb-4"
                    />

                    {/* Date Range Selection */}
                    <div className="formField form-input !mb-6">
                      <DateRangeInput
                        fromLabel={translate("From", "من")}
                        toLabel={translate("To", "إلى")}
                        fromPlaceholder={translate(
                          "Select departure date",
                          "اختر تاريخ المغادرة"
                        )}
                        toPlaceholder={translate(
                          "Select return date",
                          "اختر تاريخ العودة"
                        )}
                        onDateSelect={handleDateRangeChange}
                        initialStartDate={formData.fromDate}
                        initialEndDate={formData.toDate}
                        inline={false}
                        disableEndDate={formData.isAnnual}
                      />
                      {formData.isAnnual && formData.fromDate && (
                        <p className="text-xs text-gray-500 mt-1 text-left rtl:text-right">
                          {translate(
                            "Return date is automatically set to one year from departure.",
                            "تاريخ العودة محدد تلقائياً لمدة سنة من تاريخ المغادرة."
                          )}
                        </p>
                      )}
                      {(errors.fromDate || errors.toDate) && (
                        <p className="field-err text-red-500 text-sm mt-1 text-left rtl:text-right">
                          {errors.fromDate || errors.toDate}
                        </p>
                      )}
                    </div>

                    {/* Plan Type */}
                    <FormTabs
                      label={translate("Plan Type", "نوع الخطة")}
                      tabs={[
                        {
                          type: "individual",
                          label_en: "Individual Plan",
                          label_ar: "خطة فردية",
                        },
                        {
                          type: "family",
                          label_en: "Family Plan",
                          label_ar: "خطة عائلية",
                        },
                      ]}
                      activeTab={formData.planType}
                      onTabClick={(value) => handlePlanTypeChange(value)}
                      error={
                        formData.planType === "family" &&
                        isPrimaryUserAgeIneligibleForFamily()
                          ? translate(
                              "You are not eligible for a family policy. Members aged 66 and above are not allowed.",
                              "غير مؤهل للوثيقة العائلية. لا يُسمح للأفراد الذين تبلغ أعمارهم 66 سنة فأكثر."
                            )
                          : errors.planType
                      }
                      className="mt-4"
                    />
                  </>
                )}

              {/* Manual entry form - only show when user selects "No" for flight tickets */}
              {formData.hasFlightTickets === false && (
                <>
                  {/* Flight Number field is not shown for manual entry since users don't have tickets */}

                  {/* Annual Policy Toggle */}
                  <FormTabs
                    label={translate("Do you want an annual policy?", "هل تريد بوليصة سنوية؟")}
                    tabs={[
                      {
                        type: true,
                        label_en: "Yes",
                        label_ar: "نعم",
                      },
                      {
                        type: false,
                        label_en: "No",
                        label_ar: "لا",
                      },
                    ]}
                    activeTab={formData.isAnnual}
                    onTabClick={(value) => handleAnnualToggle(value)}
                    className="mb-4"
                  />

                  {/* Date Range Selection */}
                  <div className="formField form-input !mb-4">
                    <DateRangeInput
                      fromLabel={translate("From", "من")}
                      toLabel={translate("To", "إلى")}
                      fromPlaceholder={translate(
                        "Select departure date",
                        "اختر تاريخ المغادرة"
                      )}
                      toPlaceholder={translate(
                        "Select return date",
                        "اختر تاريخ العودة"
                      )}
                      onDateSelect={handleDateRangeChange}
                      initialStartDate={formData.fromDate}
                      initialEndDate={formData.toDate}
                      inline={false}
                      disableEndDate={formData.isAnnual}
                    />
                    {formData.isAnnual && formData.fromDate && (
                      <p className="text-xs text-gray-500 mt-1 text-left rtl:text-right">
                        {translate(
                          "Return date is automatically set to one year from departure.",
                          "تاريخ العودة محدد تلقائياً لمدة سنة من تاريخ المغادرة."
                        )}
                      </p>
                    )}
                    {(errors.fromDate || errors.toDate) && (
                      <p className="field-err text-red-500 text-sm mt-1 text-left rtl:text-right">
                        {errors.fromDate || errors.toDate}
                      </p>
                    )}
                  </div>

                  {/* Plan Type */}
                  <FormTabs
                    label={translate("Plan Type", "نوع الخطة")}
                    tabs={[
                      {
                        type: "individual",
                        label_en: "Individual Plan",
                        label_ar: "خطة فردية",
                      },
                      {
                        type: "family",
                        label_en: "Family Plan",
                        label_ar: "خطة عائلية",
                      },
                    ]}
                    activeTab={formData.planType}
                    onTabClick={(value) => handlePlanTypeChange(value)}
                    error={
                      formData.planType === "family" &&
                      isPrimaryUserAgeIneligibleForFamily()
                        ? translate(
                            "You are not eligible for a family policy. Members aged 66 and above are not allowed.",
                            "غير مؤهل للوثيقة العائلية. لا يُسمح للأفراد الذين تبلغ أعمارهم 66 سنة فأكثر."
                          )
                        : errors.planType
                    }
                  />
                </>
              )}
            </div>
          </div>
        </TravelBottomStickyBtnLayout>
      </NewTravelJourneyLayout>
    </div>
  );
};

export default TravelDateSelection;
